/**
 * Batches upload-lifecycle toasts so that bulk uploads (50+ files) don't flood
 * Sonner. Coalesces same-event toasts within a 1.5s window into a single summary.
 */
import { toast } from "sonner";

type Kind = "success" | "error" | "warning" | "info";

interface Bucket {
  kind: Kind;
  baseLabel: string; // e.g. "Uploaded", "Upload failed"
  names: string[];
  description?: string;
}

const BATCH_WINDOW_MS = 1500;
const MAX_NAMES_IN_TOAST = 3;

const buckets = new Map<string, Bucket>();
let flushTimer: number | null = null;

function flush() {
  flushTimer = null;
  for (const [, b] of buckets) {
    const count = b.names.length;
    if (count === 0) continue;
    let title: string;
    if (count === 1) {
      title = `${b.baseLabel}: ${b.names[0]}`;
    } else {
      const preview = b.names.slice(0, MAX_NAMES_IN_TOAST).join(", ");
      const extra = count > MAX_NAMES_IN_TOAST ? ` +${count - MAX_NAMES_IN_TOAST} more` : "";
      title = `${b.baseLabel} (${count}): ${preview}${extra}`;
    }
    const opts = b.description ? { description: b.description } : undefined;
    switch (b.kind) {
      case "success": toast.success(title, opts); break;
      case "error":   toast.error(title, opts); break;
      case "warning": toast.warning(title, opts); break;
      case "info":    toast.info(title, opts); break;
    }
  }
  buckets.clear();
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(flush, BATCH_WINDOW_MS);
}

function push(key: string, kind: Kind, baseLabel: string, name: string, description?: string) {
  const existing = buckets.get(key);
  if (existing) {
    existing.names.push(name);
  } else {
    buckets.set(key, { kind, baseLabel, names: [name], description });
  }
  scheduleFlush();
}

export const uploadToast = {
  uploaded(name: string) {
    push("uploaded", "success", "Uploaded", name);
  },
  duplicate(name: string) {
    push("duplicate", "info", "Already in library", name, "Skipped re-upload — same file detected.");
  },
  failed(name: string, error?: string) {
    push("failed", "error", "Upload failed", name, error);
  },
  pausedOffline() {
    // Single global notice; debounced.
    push("paused-offline", "warning", "Upload paused: network lost", "");
  },
  pausedAuth() {
    push("paused-auth", "warning", "Waiting for sign-in to continue upload", "");
  },
  resumed(name: string) {
    push("resumed", "info", "Upload resumed", name);
  },
};
